import React, { useState } from 'react';
import { Form } from 'antd';
import i18n from 'i18next';
import classNames from 'classnames';
import css from './GroupDetail.module.css';
import { FormItem } from '../../../components/Form/FormItem';
import FormInput from '../../../components/Form/FormInput';
import { updatePassword } from '../../../services';
import { SNotification } from '../../../services/notification';
import { getErrorMessage } from '../../../utils';
import { HugeiconsIcon } from '@hugeicons/react';
import { CancelCircleIcon, CheckmarkCircleIcon, Edit02Icon } from '@hugeicons-pro/core-bulk-rounded';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

const passwordToggleIcons = {
  visible: <EyeOutlined />,
  hidden: <EyeInvisibleOutlined />,
};

const ChangePasswordForm = ({ initialValues }: { initialValues?: any }) => {
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);

  const onSubmitPasswordChange = async (values: any) => {
    try {
      const { oldPassword, password, confirmPassword } = values;
      if (oldPassword && password && confirmPassword) {
        if (password !== confirmPassword) {
          throw new Error(i18n.t('msg:confirmDontMatch'));
        }
        await updatePassword({ oldPassword, password });
        SNotification.success('Change Password Success');
        setEditing(false);
        form.resetFields();
      }
    } catch (error: any) {
      SNotification.error(getErrorMessage(error) || 'Change Password Failed');
    }
  };

  return (
    <Form form={form} onFinish={onSubmitPasswordChange} layout="vertical" initialValues={initialValues}>
      <div className={css.settingsSection}>
        <div className={css.settingsAside}>
          <div className={css.settingsAsideTitle}>Security</div>
          <div className={css.settingsAsideDesc}>Update your password to keep your account secure.</div>
        </div>
        <div className={classNames(css.card, css.formCard)}>
          <div className={css.cardBody}>
            <FormItem name="oldPassword" rules={[{ required: true, message: i18n.t('msg:pleaseInput', { field: i18n.t('label:currentPassword') }) }]}>
              <FormInput label={i18n.t('label:currentPassword')} type="password" disabled={!editing} />
            </FormItem>

            <FormItem name="password" rules={[{ validator: validatePassword }]}>
              <FormInput label={i18n.t('label:newPassword')} type="password" disabled={!editing} passwordToggleIcons={passwordToggleIcons} />
            </FormItem>

            <FormItem
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: i18n.t('msg:pleaseConfirmNewPass') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) return Promise.resolve();
                    return Promise.reject(new Error(i18n.t('msg:confirmDontMatch')));
                  },
                }),
              ]}
            >
              <FormInput label={i18n.t('label:confirmNewPassword')} type="password" disabled={!editing} passwordToggleIcons={passwordToggleIcons} />
            </FormItem>
          </div>

          <div className={css.cardFooter}>
            {!editing && (
              <button type="button" className={css.btnSecondary} onClick={() => setEditing(true)}>
                <HugeiconsIcon icon={Edit02Icon} size={15} color="#374151" />
                Edit
              </button>
            )}
            {editing && (
              <>
                <button
                  type="button"
                  className={css.btnSecondary}
                  onClick={() => {
                    form.resetFields();
                    setEditing(false);
                  }}
                >
                  <HugeiconsIcon icon={CancelCircleIcon} size={15} color="#374151" />
                  Cancel
                </button>
                <button type="submit" className={css.btnPrimary}>
                  <HugeiconsIcon icon={CheckmarkCircleIcon} size={15} color="#fff" />
                  Save
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </Form>
  );
};

function validatePassword(_: any, value = '') {
  if (value.length === 0) return Promise.reject(i18n.t('msg:pleaseInput', { field: i18n.t('label:newPassword') }));
  if (value.length < 8) return Promise.reject(i18n.t('msg:passwordMinRule'));
  if (value.length > 16) return Promise.reject(i18n.t('msg:passwordMaxRule'));
  if (!value.match(/^\S*$/)) return Promise.reject(i18n.t('msg:passwordSpaceRule'));
  if (!value.match(/\d/)) return Promise.reject(i18n.t('msg:passwordDigitRule'));
  if (!value.match(/[a-z]/)) return Promise.reject(i18n.t('msg:passwordLowercaseRule'));
  if (!value.match(/[A-Z]/)) return Promise.reject(i18n.t('msg:passwordUppercaseRule'));
  if (!value.match(/[!@#$%^&*()_+\[\]\{\}\\\|\-=~`;:'"<,>.?/]/))
    return Promise.reject(i18n.t('msg:passwordSpecialRule'));
  return Promise.resolve();
}

export default ChangePasswordForm;
